import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowsOut } from '@phosphor-icons/react';

interface ZoomableImageProps {
  src?: string;
  alt?: string;
}

export const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt }) => {
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [showFullCaption, setShowFullCaption] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });
  const imgRef = React.useRef<HTMLImageElement>(null);

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImageError(true);
  };

  const handleImageLoad = () => {
    setIsLoading(true);
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      const maxHeight = 300;
      const aspectRatio = naturalWidth / naturalHeight;
      const height = Math.min(maxHeight, naturalHeight);
      const width = height * aspectRatio;
      setDimensions({ width, height });
    }
    setIsLoading(false);
  };

  if (!src || imageError) {
    return null;
  }

  return (
    <figure className="relative block my-4 px-4 w-full sm:w-auto">
      <div className="flex items-center justify-center min-h-[100px]">
        <button
          type="button"
          onClick={toggleZoom}
          className="border-0 p-0 bg-transparent relative group"
          aria-label={isZoomed ? 'Zoom out image' : 'Zoom in image'}
        >
          {isLoading && (
            <div
              className="rounded-md bg-dark-300 animate-pulse"
              style={{
                width: dimensions.width || 300,
                height: dimensions.height || 200,
                maxHeight: 300,
              }}
            />
          )}
          <img
            ref={imgRef}
            src={src}
            alt={alt}
            onError={handleImageError}
            onLoad={handleImageLoad}
            className={`max-h-[300px] w-auto object-contain cursor-zoom-in hover:opacity-90 transition-opacity ${
              isLoading ? 'opacity-0 absolute' : 'opacity-100 relative'
            }`}
          />
          {!isLoading && (
            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-black/50 text-white p-1.5 rounded-lg">
                <ArrowsOut size={16} />
              </div>
            </div>
          )}
        </button>
      </div>
      {alt && !isLoading && (
        <div className="relative mt-1">
          <figcaption
            className="text-sm text-dark-700 text-center group cursor-default"
            onMouseEnter={() => setShowFullCaption(true)}
            onMouseLeave={() => setShowFullCaption(false)}
          >
            <div className="line-clamp-2 hover:text-dark-800">{alt}</div>
          </figcaption>
          {showFullCaption && (
            <div className="absolute bottom-full left-0 right-0 mb-1 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-border text-sm text-dark-700 z-10">
              {alt}
            </div>
          )}
        </div>
      )}
      <AnimatePresence>
        {isZoomed && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50"
              onClick={toggleZoom}
            />
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="fixed inset-0 z-50 flex flex-col items-center justify-center cursor-zoom-out gap-4 p-4 sm:p-8"
              onClick={toggleZoom}
            >
              <div className="max-w-[95vw] sm:max-w-[90vw] max-h-[80vh]">
                <img src={src} alt={alt} className="w-full h-full object-contain" />
              </div>
              {alt && <div className="max-w-[95vw] sm:max-w-[90vw] text-white text-center text-sm">{alt}</div>}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </figure>
  );
};

export default ZoomableImage;
